import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { delay } from '@icp/utils';
import { restApi } from '@icp/settings';
import { fileStorageState } from '../initialState';

const selectThis = (state) => state.fileStorage;

export const selectPermissionLoading = (permissionId) =>
  createSelector(selectThis, (state) => {
    if (!permissionId) {
      return state.permissionLoading;
    }
    return state.workspaceById[permissionId]?.permissionLoading;
  });
// export const selectSessionId = createSelector(selectThis, (state) => state.sessionId);
export const selectAuthentication = (permissionId) =>
  createSelector(selectThis, (state) => {
    if (!permissionId) {
      return state.authentication;
    }
    return state.workspaceById[permissionId]?.authentication;
  });
export const selectBaseUrl = (permissionId) =>
  createSelector(selectThis, (state) => {
    if (!permissionId) {
      return state.baseUrl;
    }
    return state.workspaceById[permissionId]?.baseUrl;
  });

export const fetchUploadPermission = createAsyncThunk(
  'uploadPermission/fetchUploadPermission',
  async () => {
    return restApi.post('/form/api/form-entity-resource/workspace');
  },
  {
    condition: (payload, { getState }) => {
      const permissionLoading = selectPermissionLoading(payload.permissionId)(getState());
      return !permissionLoading;
    },
  },
);

export const uploadFile = (files, config, permissionId) => async (dispatch, getState) => {
  let baseUrl = selectBaseUrl(permissionId)(getState());
  let authentication = selectAuthentication(permissionId)(getState());

  if (!baseUrl) {
    const permissionLoading = selectPermissionLoading(permissionId)(getState());
    // 正在请求permission时，不再发起新请求，等待上个完成
    if (permissionLoading) {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await delay(100);
        const stillLoading = selectPermissionLoading(permissionId)(getState());
        if (!stillLoading) {
          baseUrl = selectBaseUrl(permissionId)(getState());
          authentication = selectAuthentication(permissionId)(getState());
          break;
        }
      }
    } else {
      const payload = await dispatch(fetchUploadPermission({ permissionId })).unwrap();
      baseUrl = payload.baseUrl;
      authentication = payload.authentication;
    }
  }

  if (process.env.NODE_ENV === 'development') {
    // in order to avoid CORS, convert to relative URL
    try {
      baseUrl = new URL(baseUrl).pathname;
    } catch {
      //
    }
  }

  const uploadUrl = `${baseUrl}/upload`;

  const formData = new FormData();

  for (const file of files) {
    formData.append('files', file);
  }

  return restApi
    .post(uploadUrl, formData, {
      ...config,
      headers: {
        ...config?.headers,
        Authorization: `Bearer ${authentication}`,
      },
    })
    .then((resData) => {
      return resData.map((item) => {
        return {
          storageId: item.tempStorageId,
          fileName: item.fileName,
          url: `${baseUrl}/file/${item.tempStorageId}?jwt=${encodeURIComponent(authentication)}`,
        };
      });
    });
};

const slice = createSlice({
  name: 'fileStorage',
  initialState: { ...fileStorageState },
  reducers: {
    resetFileStorage: (state) => {
      for (const [k, v] of Object.entries(fileStorageState)) {
        if (Array.isArray(v)) {
          state[k] = [];
        }
        if (v && typeof v === 'object') {
          state[k] = {};
        }
        state[k] = v;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUploadPermission.pending, (state, action) => {
        const { permissionId } = action.meta.arg;
        if (!permissionId) {
          state.permissionLoading = true;
        } else {
          state.workspaceById[permissionId] = { permissionLoading: true };
        }
      })
      .addCase(fetchUploadPermission.fulfilled, (state, action) => {
        const { permissionId } = action.meta.arg;
        if (!permissionId) {
          state.permissionLoading = false;
          state.sessionId = action.payload.sessionId;
          state.baseUrl = action.payload.baseUrl;
          state.authentication = action.payload.authentication;
        } else {
          state.workspaceById[permissionId] = {
            permissionLoading: false,
            sessionId: action.payload.sessionId,
            baseUrl: action.payload.baseUrl,
            authentication: action.payload.authentication,
          };
        }
      });
  },
});

export const { resetFileStorage } = slice.actions;

export default slice.reducer;
